import React, { useEffect, useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import { DialogMode } from '../../../../../Associates/Modal/TeamMemberModal';
import { TeamMember } from '../../../MyTeamMembers/Models/TeamMember';
import {
    ExternalPermissionsOptionList,
    ExternalTeamMemberRoleOptionList,
    InternalPermissionsOptionList,
    InternalTeamMemberRoleOptionList,
    TeamMemberType,
} from '../../../MyTeamMembers/Models/Enums';
import { MarketplaceTeamMemberMapping } from '../../../MyTeamMembers/Models/MarketplaceTeamMemberMapping';
import Rodal from '../../../../../../components/Rodal/Rodal';
import { useStateSelector } from '../../../../../../store/selectors';
import createNotification from '../../../../../../utils/createNotification';

interface AddOrEditTeamMemberPopupProps {
    isVisible: boolean;
    onClose: () => void;
    mode: DialogMode;
    mapping?: MarketplaceTeamMemberMapping;
    opportunityId: number;
    teamMember?: TeamMember;
    membersWithoutAccessToOpportunity?: TeamMember[];
}

export const AddOrEditTeamMemberPopup = (
    props: AddOrEditTeamMemberPopupProps
) => {
    const [selectedCreateMemberId, setSelectedCreateMemberId] = useState<
        number | undefined
    >();
    const [selectedPermission, setSelectedPermission] = useState<number>();
    const axios = useStateSelector((state) => state.core.axios);

    const renderCreateForm = () => {
        const selectedCreateMember =
            props.membersWithoutAccessToOpportunity?.find(
                (m) => m.id === selectedCreateMemberId
            );

        const selectedMapping = selectedCreateMember
            ? selectedCreateMember.associateMemberMarketplaceMappings?.find(
                  (map: any) => map.isGeneralSettings
              )
            : undefined;

        const isInternal = selectedMapping?.type === TeamMemberType.Internal;
        const options = isInternal
            ? InternalPermissionsOptionList
            : ExternalPermissionsOptionList;

        return (
            <div className="edit-form">
                <div className="row mb-3">
                    <div className="col">
                        <label htmlFor="team-member-select">
                            <strong>Team Member:</strong>
                        </label>
                        <select
                            id="team-member-select"
                            className="form-control"
                            value={selectedCreateMemberId ?? ''}
                            onChange={(e) => {
                                setSelectedCreateMemberId(
                                    Number(e.target.value)
                                );
                                setSelectedPermission(undefined);
                            }}>
                            <option value="">Select team member</option>
                            {props.membersWithoutAccessToOpportunity?.map(
                                (m) => {
                                    const mapping =
                                        m.associateMemberMarketplaceMappings?.find(
                                            (map: any) => map.isGeneralSettings
                                        );
                                    const isInternal =
                                        mapping?.type ===
                                        TeamMemberType.Internal;
                                    const roleLabel = isInternal
                                        ? InternalTeamMemberRoleOptionList.find(
                                              (r) =>
                                                  r.value ===
                                                  mapping?.internalRole
                                          )?.label
                                        : ExternalTeamMemberRoleOptionList.find(
                                              (r) =>
                                                  r.value ===
                                                  mapping?.externalRole
                                          )?.label;
                                    return (
                                        <option key={m.id} value={m.id}>
                                            {m.name}{' '}
                                            {roleLabel ? `- ${roleLabel}` : ''}
                                        </option>
                                    );
                                }
                            )}
                        </select>
                    </div>
                </div>
                {selectedCreateMember && (
                    <div className="row mb-3">
                        <div className="col">
                            <label htmlFor="permission-select">
                                <strong>Permissions:</strong>
                            </label>
                            <select
                                id="permission-select"
                                className="form-control"
                                value={selectedPermission ?? ''}
                                onChange={(e) =>
                                    setSelectedPermission(
                                        Number(e.target.value)
                                    )
                                }>
                                <option value="">Select permission</option>
                                {options.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (props.mode === DialogMode.Create) {
        } else {
            const permission =
                props.mapping.type === TeamMemberType.Internal
                    ? props.mapping.internalPermissions
                    : props.mapping.externalPermissions;
            setSelectedPermission(permission);
        }
    }, []);

    const renderEditForm = () => {
        const isInternal = props.mapping.type === TeamMemberType.Internal;
        const options = isInternal
            ? InternalPermissionsOptionList
            : ExternalPermissionsOptionList;

        const roleLabel = isInternal
            ? InternalTeamMemberRoleOptionList.find(
                  (r) => r.value === props.mapping?.internalRole
              ).label
            : ExternalTeamMemberRoleOptionList.find(
                  (r) => r.value === props.mapping?.externalRole
              ).label;
        return (
            <div className="edit-form">
                <div className="row mb-3">
                    <div className="col">
                        <strong>Team Member:</strong> {props.teamMember?.name}
                        {' - '}
                        {roleLabel}
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col">
                        <label htmlFor="permission-select">
                            <strong>Permissions:</strong>
                        </label>
                        <select
                            id="permission-select"
                            className="form-control"
                            value={selectedPermission}
                            onChange={(e) =>
                                setSelectedPermission(Number(e.target.value))
                            }>
                            <option value="">Select permission</option>
                            {options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    };

    const renderButtonsSection = () => {
        const isSubmitDisabled = !selectedPermission;
        return (
            <div className="buttons-section">
                <button className="btn cancel-btn" onClick={props.onClose}>
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={() => {
                        props.mode === DialogMode.Create
                            ? createTeamMember()
                            : changeAccessRights();
                    }}
                    disabled={isSubmitDisabled}>
                    {props.mode === DialogMode.Create
                        ? 'Submit'
                        : 'Change Access'}
                </button>
            </div>
        );
    };

    const createTeamMember = () => {
        axios
            .post(`/api/TeamMembers`, {
                opportunityId: props.opportunityId,
                teamMemberId: selectedCreateMemberId,
                internalPermissions: selectedPermission,
                externalPermissions: selectedPermission,
            })
            .catch((error: any) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                props.onClose();
            });
    };

    const changeAccessRights = () => {
        axios
            .put(`/api/TeamMembers`, {
                opportunityId: props.opportunityId,
                teamMemberId: props.teamMember?.id,
                permissions: selectedPermission,
                internalPermissions: selectedPermission,
                externalPermissions: selectedPermission,
            })
            .catch((error: any) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                props.onClose();
            });
    };

    return (
        <Rodal
            width={550}
            height={500}
            visible={props.isVisible}
            animation={'slideRight'}
            showCloseButton={true}
            onClose={props.onClose}
            showMask={false}
            className={'add-or-edit-team-member-popup'}>
            <ModalHeader>
                {props.mode === DialogMode.Create
                    ? 'Add Team Member'
                    : 'Edit Team Member'}
            </ModalHeader>
            <ModalBody>
                {props.mode === DialogMode.Create
                    ? renderCreateForm()
                    : renderEditForm()}
                {renderButtonsSection()}
            </ModalBody>
        </Rodal>
    );
};
